import { Box, Link, Stack, Typography } from "@mui/material";
import { TbShirtSport } from "react-icons/tb";
import { HiOutlineTicket } from "react-icons/hi";
import { MdOutlineEventSeat, MdOutlinedFlag } from "react-icons/md";

import { routes } from "@/global/routes";
import { DefaultCheckoutTerms } from "../../default_terms_and_conditions";
import { throttleTime } from "@/global/utils/utils";
import { useTeamConfig } from "@/global/hooks/useTeamConfig";
import { useGenericModal } from "@/shared/common/GenericModal";
import { useRouterExtended } from "@/global/hooks/useRouterExtended";
import { useCssMediaQuery } from "@/shared/base/hooks/useCssMediaQuery";
import { TransferTicketButton } from "@/shared/modules/ticketTransfer/TransferTicketButton";
import { ModalActionButton } from "@/shared/base/components/ModalActionButton/ModalActionButton";

const accountManagerUrl = "https://am.ticketmaster.com/msg/";
const salesPhone = "2124656080";
const salesPhoneDisplay = "(212) 465-6080";

export const howItWorks = [
  {
    icon: <MdOutlinedFlag />,
    title: "Choose Your Games ",
    description: "Select the games you wish to attend with your group.",
  },
  {
    icon: <TbShirtSport />,
    title: "Login to Account Manager",
    description: (
      <>
        Click Continue and connect to your{" "}
        <Link
          href={accountManagerUrl}
          target={"_blank"}
          sx={{
            color: "primary.main",
            textDecoration: "none",
            fontWeight: 500,
          }}
        >
          New York Rangers
        </Link>{" "}
        account using our secure Ticketmaster login.
      </>
    ),
  },
  {
    icon: <MdOutlineEventSeat />,
    title: "Select Your Seats",
    description: "Select the seats you wish to purchase for your group.",
  },
  {
    icon: <HiOutlineTicket />,
    title: "Get Your Tickets",
    description: (
      <>
        Tickets will be available to be managed in your{" "}
        <Link
          target={"_blank"}
          sx={{
            fontWeight: 500,
            color: "primary.main",
            textDecoration: "none",
          }}
          href={accountManagerUrl}
        >
          New York Rangers
        </Link>{" "}
        Account Manager within 24 hours. Contact our Sales Team at{" "}
        <Link
          sx={{
            fontWeight: 500,
            color: "primary.main",
            textDecoration: "none",
          }}
          href={`tel:${salesPhone}`}
        >
          {salesPhoneDisplay}
        </Link>{" "}
        for any questions.
      </>
    ),
  },
];

export const faqs = [
  {
    title: "What is a group ticket sale?",
    description:
      "Bring your friends, family, coworkers, or clients to the Garden. Group Sales lets you purchase seats together for the games you choose (subject to availability).",
  },
  {
    title: "What is the minimum number of tickets for a group?",
    description: "Group Sales are a minimum of 9 tickets.",
  },
  {
    title: "I want to purchase 20+ tickets",
    description: (
      <>
        If you&apos;d like assistance from a representative for purchases of 20
        or more tickets, please email{" "}
        <Link
          href="mailto:Group.Sales@msg.com"
          sx={{
            color: "primary.main",
            textDecoration: "none",
            fontWeight: 500,
          }}
        >
          Group.Sales@msg.com
        </Link>{" "}
        or call{" "}
        <Link
          href={`tel:${salesPhone}`}
          sx={{
            color: "primary.main",
            textDecoration: "none",
            fontWeight: 500,
          }}
        >
          212-465-6080
        </Link>
        .
      </>
    ),
  },
  {
    title: "Can I transfer or resell my tickets if I can no longer attend?",
    description:
      "You may transfer your tickets as needed but resale is not permitted.",
  },
  {
    title: "When can I manage/transfer my tickets?",
    description:
      "Ticket management will be available on or around the 15th of the month prior to your game.",
  },
];

export const CheckoutTerms = () => (
  <>
    <DefaultCheckoutTerms /> You also agree to the{" "}
    <Link
      href="https://am.ticketmaster.com/msg/en/nyr/2627/groups-terms"
      target={"_blank"}
      rel={"noreferrer"}
      sx={{
        color: "primary.main",
        textDecoration: "none",
        fontWeight: 500,
      }}
    >
      New York Rangers&apos; Terms &amp; Conditions
    </Link>
    .
    <Box sx={{ mt: 1, fontWeight: 700 }}>
      Group Sales tickets are not permitted for resale.
    </Box>
  </>
);

export function CheckoutSuccessDescription() {
  const teamConfig = useTeamConfig();
  const { md: mdUp } = useCssMediaQuery("up");
  const router = useRouterExtended();
  const viewTicketsModal = useGenericModal();

  const onClickViewTicket = () => {
    viewTicketsModal.set({
      isOpen: true,
      title: <Box height="32px"></Box>,
      hideTitleIcon: true,
      description: (
        <Box>
          <Typography variant="body1" sx={{ mb: 3 }}>
            By clicking View Tickets, you will be redirected to the New York
            Rangers Account Manager to access your barcodes. Please note that
            any ticket transfers or forwarding completed there will not be
            reflected in this portal. For a seamless experience, please manage
            all transfers and forwarding through the Transfer Tickets portal.
          </Typography>
        </Box>
      ),

      closeIconProps: {
        onClick: () => {
          viewTicketsModal.set({ isOpen: false });
          throttleTime(400, () => {
            viewTicketsModal.reset();
          });
        },
      },

      closeButtonText: "View Tickets",
      closeButtonProps: {
        variant: "outlined",
      },
      onClickClose: () => {
        window.location.href = teamConfig.accountManagerUrl;
      },

      confirmButtonText: "Transfer Tickets",
      onClickConfirm: async () => {
        await router.push({
          pathname: routes.ticket_transfer,
          query: {
            ...router.query,
            eventCode: router?.query?.eventCode as string,
            orderNumber: Number(router?.query?.orderNumber),
          },
        });
      },
    });
  };

  return (
    <Box>
      <Typography fontSize={"1.1rem"} color={"text.primary"} lineHeight={1.4}>
        Your order was successful! If you&apos;d like to transfer tickets to
        members of your group, click Transfer Tickets to get started.
      </Typography>
      <Stack
        direction={mdUp ? "row" : "column"}
        justifyContent={"center"}
        alignItems="center"
        spacing={2}
        mt={2.5}
      >
        <TransferTicketButton
          size="large"
          eventCode={router?.query?.eventCode as string}
          orderNumber={Number(router?.query?.orderNumber)}
          sx={{
            width: mdUp ? "inherit" : "100%",
          }}
        />

        <ModalActionButton
          variant="outlined"
          onClick={onClickViewTicket}
          size="large"
          sx={{
            width: mdUp ? "170px" : "100%",
          }}
        >
          View Tickets
        </ModalActionButton>
      </Stack>
    </Box>
  );
}

export function NoInventoryModalDescription() {
  return (
    <Typography variant="body1">
      It looks like this event is running low on inventory. Please call{" "}
      <Link
        className="global-link"
        fontWeight={"500"}
        href={`tel:${salesPhone}`}
      >
        212-465-6080
      </Link>{" "}
      for available options.
    </Typography>
  );
}
